import React, { useState } from "react"
import SEO from "../components/seo"
import Banner from "../components/banner/banner"
import PageBody from "../components/page-body/pageBody"
import BorderTitle from "../components/border-title/borderTitle"
import Button from "../components/button/button"
import { Link } from "gatsby"
import bg from "../images/banners/about-our-sponsors.JPG"

import "./about-mhsc.scss"

const AboutMHSCPage = () => {
  const [activeTab, setActiveTab] = useState("town-halls")

  const tabContent = {
    "town-halls": {
      title: "Town Halls",
      description: "Learn about the Town Halls held to share program outcomes with community members and leaders, including powerful testimonies from coaches and participants.",
      link: "/mhsc/town-halls",
      linkText: "Learn More About Town Halls"
    },
    "outcomes": {
      title: "Program Outcomes",
      description: "Discover the astounding outcomes of the MHSC Program, including significant improvements in mental health, quality of life, and well-being for participants.",
      link: "/mhsc/outcomes",
      linkText: "Learn More About Outcomes"
    },
    "coaches": {
      title: "Mental Health Coaches",
      description: "Hear from our Mental Health Support Coaches, read their quotes, and learn about their experiences leading coaching sessions.",
      link: "/booking",
      linkText: "Learn More About Coaches"
    },
    "recruitment": {
      title: "Recruitment & Enrollment",
      description: "Learn about who we are actually serving—the participants in our program, their backgrounds, and the communities we reach.",
      link: "/mhsc/recruitment-enrollment",
      linkText: "Learn More About Participants"
    }
  }

  return (
  <>
    <SEO 
      title="About Virtual Mental Health Support Coaching" 
      description="Learn about the Virtual Mental Health Support Coaching (MHSC) program - its history, development, need, and impact on Black youth and adults in lower-income communities."
      keywords={[`about`, `mhsc`, `vmhsc`, `mental health`, `coaching`, `wellness`, `support`, `history`]} 
    />
    <div className="about-mhsc-page">
      <Banner
        src={bg}
        title="ABOUT VIRTUAL MENTAL HEALTH SUPPORT COACHING"
        subTitle="The History, Developments, and Impact"
        height="27.5rem"
        backgroundPosY="20%"
      />
      <PageBody className="page">
        <div className="program-history">
          <BorderTitle className="title">PROGRAM HISTORY</BorderTitle>
          <p className="history-description">
            The Virtual Mental Health Support Coaching (MHSC) Program was designed by Dr. Carolyn M. Tucker at the University of Florida. This novel program has been found to be effective in significantly reducing commonly experienced stress, depression, grief, feelings of anger, and loneliness, and in improving quality of life, social support, and well-being in Black youth and adults. The program was made possible through the generous funding and support of the Florida Blue Foundation.
          </p>
          
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-date">Year 1 (2022)</div>
              <div className="timeline-content">
                <h4 className="timeline-title">34 Youth 111 Adults</h4>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">Year 2 (2023)</div>
              <div className="timeline-content">
                <h4 className="timeline-title">77 Youth 158 Adults</h4>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">Year 3 (2024)</div>
              <div className="timeline-content">
                <h4 className="timeline-title">115 Youth 146 Adults</h4>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">Year 4 (2025)</div>
              <div className="timeline-content">
                <h4 className="timeline-title">Focus on Sustainability and Dissemination</h4>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">September 18, 2025</div>
              <div className="timeline-content">
                <h4 className="timeline-title">Town Hall at Springhill Church</h4>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">October 11, 2025</div>
              <div className="timeline-content">
                <h4 className="timeline-title">Virtual Town Hall for Youth</h4>
              </div>
            </div>
          </div>
        </div>

        <div className="program-need">
          <BorderTitle className="title">THE NEED FOR THE MHSC PROGRAM</BorderTitle>
          <p className="need-description">
            The MHSC program was developed in response to the prevalence of stress, loneliness, depression, and grief that negatively impacts the mental health and well-being of adults and youth, particularly those living in low-income, unsafe, minoritized, and/or food-insecure communities. Other sources of impetus for developing this program include:
          </p>
          <ul className="need-list">
            <li>The unavailability and unaffordability of mental health counseling in lower-income communities</li>
            <li>The often culture-related stigma toward seeking mental health counseling that exists among many Black adults and youth</li>
            <li>The alarming rates of suicide and suicide attempts among Black youth</li>
          </ul>
          <p className="need-stats">
            Notably, in 2021 suicide was the third leading cause of death among Black youth between the ages of 10 to 24 years old. The CDC's Youth Risk Behavior Survey for 1991 to 2017 demonstrated that suicide attempts among Black youth increased by 73% over that period, while attempts among every other racial/ethnic group decreased. Black adults show similar alarming trends. From 2018 to 2021, it was found that for adults ages 25 to 44 years old, suicide rates increased by 22.9% among Blacks.
          </p>
          <p className="need-conclusion">
            These statistics reflect the broader impact of social determinants of health—poverty, discrimination, food insecurity, and limited educational opportunities. MHSC coaches and participants are largely recruited from lower-income communities, and the need for mental health support is evident in the high levels of grief, depression, loneliness, feelings of anger, and stress observed among participants.
          </p>
          <div className="need-references">
            <p className="references-title">References</p>
            <ol className="references-list">
              <li>Black youth suicide prevention initiative. (2024, August 27). <a href="https://www.samhsa.gov/mental-health/suicidal-behavior/prevention-initiatives/black-youth-suicide" target="_blank" rel="noopener noreferrer">https://www.samhsa.gov/mental-health/suicidal-behavior/prevention-initiatives/black-youth-suicide</a></li>
              <li>Bommersbach, T. J., Olfson, M., & Rhee, T. G. (2025). National Trends in Suicidal Thoughts and Suicide Attempts Among High School Students in the United States. <em>The American journal of psychiatry</em>, 182(7), 639–659. <a href="https://doi.org/10.1176/appi.ajp.20240706" target="_blank" rel="noopener noreferrer">https://doi.org/10.1176/appi.ajp.20240706</a></li>
              <li>Stone DM, Mack KA, Qualters J. Notes from the Field: Recent Changes in Suicide Rates, by Race and Ethnicity and Age Group — United States, 2021. <em>MMWR Morb Mortal Wkly Rep</em> 2023;72:160–162. DOI: <a href="http://dx.doi.org/10.15585/mmwr.mm7206a4" target="_blank" rel="noopener noreferrer">http://dx.doi.org/10.15585/mmwr.mm7206a4</a>.</li>
            </ol>
          </div>
        </div>

        <div className="learn-more-sections">
          <BorderTitle className="title">LEARN MORE ABOUT</BorderTitle>
          <div className="tabs-container">
            <div className="tabs-sidebar">
              <button
                className={`tab-button ${activeTab === "town-halls" ? "active" : ""}`}
                onClick={() => setActiveTab("town-halls")}
              >
                TOWN HALLS
              </button>
              <button
                className={`tab-button ${activeTab === "recruitment" ? "active" : ""}`}
                onClick={() => setActiveTab("recruitment")}
              >
                RECRUITMENT & ENROLLMENT
              </button>
              <button
                className={`tab-button ${activeTab === "outcomes" ? "active" : ""}`}
                onClick={() => setActiveTab("outcomes")}
              >
                PROGRAM OUTCOMES
              </button>
            </div>
            <div className="tabs-content">
              <div className="tab-panel">
                <h3 className="tab-panel-title">{tabContent[activeTab].title}</h3>
                <p className="tab-panel-description">{tabContent[activeTab].description}</p>
                <Link to={tabContent[activeTab].link}>
                  <Button className="tab-learn-more-button">
                    {tabContent[activeTab].linkText}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="sustainability-section">
          <BorderTitle className="title">SUSTAINING AND REPLICATING THE MHSC PROGRAM</BorderTitle>
          <p className="sustainability-description">
            In the fourth and final year of this Program, Dr. Tucker and her research team have shifted their focus towards achieving three objectives: evaluating the impact of the MHSC Program on reducing stigma, ensuring program sustainability, and disseminating the program throughout Florida.
          </p>
          <div className="sustainability-items">
            <div className="sustainability-item">
              <h4 className="item-title">Reducing Stigma</h4>
              <p>Launching a Social Media Campaign and evaluating attitudes towards seeking professional mental health services.</p>
            </div>
            <div className="sustainability-item">
              <h4 className="item-title">Program Sustainability</h4>
              <p>Certifying adult Mental Health Support Coaching Program Implementation Coaches and providing tools for long-term program delivery.</p>
            </div>
            <div className="sustainability-item">
              <h4 className="item-title">Program Replication</h4>
              <p>Promoting replication throughout Florida with an online registry for organizations interested in receiving MHSC Program materials.</p>
            </div>
          </div>
        </div>

        <div className="get-involved-section">
          <BorderTitle className="section-title">GET INVOLVED</BorderTitle>
          <p className="get-involved-text">
            Ready to take the next step? Book a coaching session or learn more about implementing the program in your community.
          </p>
          <div className="cta-buttons">
            <Link to="/book-appointment">
              <Button className="enroll-button">
                Book a Coaching Session
              </Button>
            </Link>
            <Link to="/replicate-health-smart">
              <Button className="learn-more-button">
                Replicate the Health-Smart PROGRAM
              </Button>
            </Link>
          </div>
        </div>
      </PageBody>
    </div>
  </>
  )
}

export default AboutMHSCPage


